import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TablesTranslations} from '~shared/components/tables/tables-translations.service';
import {SidebarService} from '~core/components/sidebar/sidebar.service';
import {LotteryStatusTranslationService, TooltipTranslationService} from '~shared/translations';
import {ActionButtonType} from '~core/enum';
import {ClubService, NavigationService, StorageService, TixxSelectService} from '~core/services';
import {ActionButton, ActionButtonEvent, AuthenticatedUser, Club, Lottery, SidebarOptions} from '~core/models';
import {SpinnerService} from "~shared/components/buttons/spinner/spinner.service";

/**
 * Operate the attributes on a certain club-overview
 */
@UntilDestroy()
@Component({
  templateUrl: './tixx-select.component.html',
})
export class TixxSelectComponent implements OnDestroy, OnInit {
  public authenticatedUser: AuthenticatedUser;
  public selectedClub: Club;

  public i18nEmptyState: string;
  public i18nHeading: string;
  public i18nHeadingButton: string;
  public i18nSearchPlaceholder: string;
  public actionButtons: Array<ActionButton> = [];

  public baseApi: string;
  public customQueries: Array<any> = [];

  public columns: Array<string> = [];
  public data: Array<Lottery> = [];
  public keys: Array<Array<string>> = [];

  public isModalOpened: boolean = false;
  public spinner: string = "newTixxSelect";

  constructor(
    public clubService: ClubService,
    public route: ActivatedRoute,
    public storage: StorageService,
    public tablesTranslations: TablesTranslations,
    public sidebarService: SidebarService,
    public navigationService: NavigationService,
    public tooltipTranslations: TooltipTranslationService,
    private tixxSelectService: TixxSelectService,
    public router: Router,
    private lotteryTranslations: LotteryStatusTranslationService,
    public spinnerService: SpinnerService
  ) {
    this.sidebarService.setSidebar(new SidebarOptions(false, false));
    this.navigationService.setCurrentPage('tixxSelectOverview');
    this.navigationService.setCurrentBreadcrumbs(['crm', 'tixxSelectOverview']);
    this.storage.castAuthenticatedUser.pipe(untilDestroyed(this))
      .subscribe((user: AuthenticatedUser) => this.authenticatedUser = user);
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe((club: Club) => this.selectedClub = club);
    this.tixxSelectService.castLotteryList.pipe(untilDestroyed(this))
      .subscribe((lotteries: Array<Lottery>) => this.data = lotteries);
  }

  public ngOnDestroy(): void {
  }

  public ngOnInit(): void {
    this.i18nHeading = $localize`:@@tixxSelectComponent.heading.tixxSelectEvents:Tixx-Select Events`;
    this.i18nHeadingButton = $localize`:@@general.button.createNew:Create new`;
    this.i18nSearchPlaceholder = $localize`:@@general.formLabel.searchOrders:Search events`;
    this.i18nEmptyState = $localize`:@@general.emptyState.noTixxEvents:There are no Tixx-Select events`;
    this.setUp();
    this.getLotteries(this.selectedClub.id);
  }

  /**
   * Adds the columns and keys for the table
   */
  private setUp(): void {
    this.columns.push(
      this.tablesTranslations.i18nDate,
      this.tablesTranslations.i18nCompetition,
      this.tablesTranslations.i18nName,
      this.tablesTranslations.i18nHomeTeam,
      this.tablesTranslations.i18nGuestTeam,
      this.tablesTranslations.i18nStatus,
    );
    this.keys.push(['event.date', 'event.competition', 'event.name', 'event.homeTeam', 'event.guestTeam', 'status.status']);
    this.actionButtons.push(
      new ActionButton({
        type: ActionButtonType.EDIT,
        icon: '#esf-icon-edit',
        tooltip: this.tooltipTranslations.i18nEdit,
      }),
      new ActionButton({
        type: ActionButtonType.REDIRECT,
        icon: '#esf-icon-maximize',
        tooltip: this.tooltipTranslations.i18nOpenWebpage,
      })
    );
  }

  /**
   * Get the reminders for the current customer.
   */
  public getLotteries(id: number): any {
    const endpoint = this.tixxSelectService.getLotteriesEndpoint(id);
    this.baseApi = endpoint[0];
    this.customQueries = endpoint[1];
  }

  /**
   * Sets the new reminder list
   */
  public showLotteries(lotteries: Array<Lottery>): void {
    const ltr = lotteries.map(lottery => new Lottery(lottery, this.lotteryTranslations));
    this.tixxSelectService.setLotteryList(ltr);
  }

  /**
   * The row was clicked so we will open the tixx select overview
   */
  public rowClicked(event: any): void {
    this.tixxSelectService.setSelectedLottery(event);
    this.router.navigate(['club', this.selectedClub.id, 'tixx-select-overview', event.id]);
  }

  /**
   * Checks which action button was clicked
   */
  public actionButtonClicked(actionButton: ActionButtonEvent<any>): void {
    switch (actionButton.type) {
      case ActionButtonType.EDIT:
        this.rowClicked(actionButton.data);
        break;
      case ActionButtonType.REDIRECT:
        window.open(actionButton.data.website.lotteryEventUrl);
        break;
      default:
    }
  }

}
